// ============================================================================
// PV-1052 · M8 Patch 5b — public seller submission photo collector
// Refs: Spec V1.1 §M8 L1179, L1196-1198 · UI Behavior V1.1 §6.3 L1243-1263
//
// Controlled component. Collects up to `max` File objects in memory; the
// parent submits them as multipart fields photo_0..photo_N to the Edge
// Function `seller-submission-create`. No upload happens here — only client
// validation (MIME, size, count) and local preview thumbnails.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import type { TFunction } from 'i18next';

import './SellerPhotoUploader.css';

const ACCEPT = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
};

export const MAX_PHOTOS = 5; // Spec §M8 L1179
export const MAX_PHOTO_BYTES = 5 * 1024 * 1024; // 5 MiB — Spec §M8 L1179

export type SellerPhotoUploaderError = 'too_large' | 'invalid_type' | 'too_many';

export type SellerPhotoUploaderProps = {
  value: ReadonlyArray<File>;
  onChange: (next: File[]) => void;
  max?: number;
  /** Reports an upstream-actionable rejection (toast / inline). */
  onError?: (kind: SellerPhotoUploaderError) => void;
  t: TFunction;
  /** Optional id forwarded to the underlying input — useful for label association. */
  inputId?: string;
};

export function SellerPhotoUploader({
  value,
  onChange,
  max = MAX_PHOTOS,
  onError,
  t,
  inputId,
}: SellerPhotoUploaderProps) {
  // Cache object URLs per File reference so we don't churn createObjectURL
  // on every render. Cleanup runs when files leave `value` and at unmount.
  const urlCacheRef = useRef<Map<File, string>>(new Map());

  const previews = useMemo(() => {
    return value.map((file) => {
      const cached = urlCacheRef.current.get(file);
      if (cached) return { file, url: cached };
      const url = URL.createObjectURL(file);
      urlCacheRef.current.set(file, url);
      return { file, url };
    });
  }, [value]);

  // Sweep entries that are no longer referenced by `value`.
  useEffect(() => {
    const present = new Set(value);
    const cache = urlCacheRef.current;
    for (const [file, url] of cache.entries()) {
      if (!present.has(file)) {
        URL.revokeObjectURL(url);
        cache.delete(file);
      }
    }
  }, [value]);

  // Final cleanup at unmount.
  useEffect(() => {
    const cache = urlCacheRef.current;
    return () => {
      for (const url of cache.values()) URL.revokeObjectURL(url);
      cache.clear();
    };
  }, []);

  const remainingSlots = Math.max(0, max - value.length);
  const isCapReached = remainingSlots === 0;

  const onDrop = useCallback(
    (accepted: File[], rejections: FileRejection[]) => {
      if (rejections.length > 0) {
        const code = rejections[0]?.errors[0]?.code;
        if (code === 'file-too-large') onError?.('too_large');
        else if (code === 'file-invalid-type') onError?.('invalid_type');
        else if (code === 'too-many-files') onError?.('too_many');
        else onError?.('invalid_type');
      }

      if (accepted.length === 0) return;

      if (accepted.length > remainingSlots) onError?.('too_many');
      const cappedAccepted = accepted.slice(0, remainingSlots);
      if (cappedAccepted.length === 0) return;

      onChange([...value, ...cappedAccepted]);
    },
    [value, onChange, onError, remainingSlots],
  );

  const dropzone = useDropzone({
    accept: ACCEPT,
    maxSize: MAX_PHOTO_BYTES,
    disabled: isCapReached,
    onDrop,
  });

  const handleRemove = useCallback(
    (index: number) => {
      const next = value.filter((_, i) => i !== index);
      onChange(next);
    },
    [value, onChange],
  );

  const dropzoneClass = useMemo(() => {
    const parts = ['seller-photo-uploader__dropzone'];
    if (dropzone.isDragActive) parts.push('is-drag-active');
    if (isCapReached) parts.push('is-disabled');
    return parts.join(' ');
  }, [dropzone.isDragActive, isCapReached]);

  return (
    <div className="seller-photo-uploader">
      <div {...dropzone.getRootProps({ className: dropzoneClass })} aria-disabled={isCapReached}>
        <input {...dropzone.getInputProps({ id: inputId })} />
        <div className="seller-photo-uploader__title">{t('sell.form.photos.dropzoneTitle')}</div>
        <div className="seller-photo-uploader__sub">{t('sell.form.photos.dropzoneHint')}</div>
        <div className="seller-photo-uploader__standards">
          {t('sell.form.photos.dropzoneStandardsHint')}
        </div>
      </div>

      {previews.length > 0 && (
        <ul className="seller-photo-uploader__grid" aria-label={t('sell.form.photos.listLabel')}>
          {previews.map((entry, index) => (
            <li key={entry.url} className="seller-photo-uploader__item">
              <img
                src={entry.url}
                alt={t('sell.form.photos.thumbnailAlt', { position: index + 1 })}
                className="seller-photo-uploader__thumb"
                width={120}
                height={80}
                loading="lazy"
              />
              <button
                type="button"
                className="seller-photo-uploader__remove"
                aria-label={t('sell.form.photos.removeAria', { position: index + 1 })}
                onClick={() => handleRemove(index)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="seller-photo-uploader__counter" aria-live="polite">
        {t('sell.form.photos.counter', { count: value.length, max })}
      </div>
    </div>
  );
}
